import { useEffect, useMemo } from 'react';
import { AppBar, Toolbar, Typography, Box, Button, Stack, Container, ThemeProvider } from '@mui/material';
import { getWebsiteTheme } from '../../theme/websiteTheme';
import { useAuthShell } from './useAuthShell';
import logo from '../../assets/logo.png';

const NavLink = ({
  label, path, activePath, onClick,
}: { label: string; path: string; activePath: string; onClick: (p: string) => void }) => {
  const active = activePath === path;
  return (
    <Typography
      role="link"
      tabIndex={0}
      onClick={() => onClick(path)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(path); }}
      sx={{
        cursor: 'pointer',
        fontWeight: 700,
        fontSize: 14,
        px: 1.75,
        py: 0.85,
        borderRadius: '999px',
        whiteSpace: 'nowrap',
        color: active ? '#fff' : 'text.secondary',
        background: active ? 'linear-gradient(135deg, #6226A7, #B06AB3)' : 'transparent',
        boxShadow: active ? '0 6px 16px rgba(98,38,167,0.4), inset 0 1px 0 rgba(255,255,255,.45)' : 'none',
        transition: 'background .15s, color .15s, transform .15s',
        '&:hover': { bgcolor: active ? undefined : 'rgba(98,38,167,0.08)', transform: 'translateY(-1px)' },
      }}
    >
      {label}
    </Typography>
  );
};

const PublicHeader = ({ children }: { children: React.ReactNode }) => {
  const { active, isAuthed, go } = useAuthShell();
  // Home, About and Contact lay themselves out full-width (they manage their own max-width).
  const isHome = active === '/' || active === '/about' || active === '/contact';
  // The public pages (home / login / about / contact) are designed on light surfaces only. Keep them on the
  // light theme even when the signed-in app is in dark mode, otherwise text turns white on white.
  const lightTheme = useMemo(() => getWebsiteTheme('light'), []);

  // Reset the section colours so a stale section (e.g. Payments green) never leaks onto public pages.
  useEffect(() => {
    document.documentElement.style.setProperty('--qs-a', '#6226A7');
    document.documentElement.style.setProperty('--qs-b', '#F6A938');
  }, []);

  return (
    <ThemeProvider theme={lightTheme}>
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', colorScheme: 'light', display: 'flex', flexDirection: 'column' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.8)',
          color: 'text.primary',
          borderRadius: 0,
          border: 0,
          backdropFilter: 'saturate(180%) blur(16px)',
          boxShadow: '0 10px 30px rgba(98,38,167,0.10)',
          '&::after': {
            content: '""', position: 'absolute', left: 0, right: 0, bottom: 0, height: 3,
            background: 'linear-gradient(90deg,#6226A7,#EC4899,#F6A938)',
          },
        }}
      >
        <Toolbar sx={{ minHeight: 64, gap: 1.5 }}>
          <Box onClick={() => go('/')} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, cursor: 'pointer' }}>
            <Box component="img" src={logo} alt="QuillStack" sx={{ width: 34, height: 34, borderRadius: '10px' }} />
            <Typography variant="h6" sx={{ fontWeight: 900, fontSize: { xs: 17, md: 19 }, letterSpacing: '-0.01em' }}>QuillStack</Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'nowrap' }}>
            <NavLink label="Home" path="/" activePath={active} onClick={go} />
            <NavLink label="About us" path="/about" activePath={active} onClick={go} />
            <NavLink label="Contact us" path="/contact" activePath={active} onClick={go} />
            {isAuthed ? (
              <Button onClick={() => go('/dashboard')} variant="contained" color="primary" sx={{ ml: 1, borderRadius: '999px', px: 2.5 }}>
                Go to Dashboard
              </Button>
            ) : (
              <Button onClick={() => go('/login')} variant="contained" color="primary" sx={{ ml: 1, borderRadius: '999px', px: 2.5 }}>
                Login
              </Button>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      {isHome ? (
        <Box key={active} className="qs-animate-in" sx={{ flex: 1, width: '100%' }}>{children}</Box>
      ) : (
        <Container key={active} className="qs-animate-in" maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, sm: 3 }, flex: 1, width: '100%' }}>
          {children}
        </Container>
      )}

      <Box component="footer" sx={{ py: { xs: 2, md: 3 }, bgcolor: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(10px)', borderTop: '1px solid rgba(98,38,167,0.10)' }}>
        <Box sx={{ maxWidth: 1536, mx: 'auto', px: { xs: 2, sm: 3 } }}>
          <Typography variant="body2" color="text.secondary" align="center">
            © {new Date().getFullYear()} QuillStack. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
    </ThemeProvider>
  );
};

export default PublicHeader;
